import { IconReports, IconWebsiteAudit, IconGoogleSearch, IconGoogleBusiness, IconSEOIntelligence, IconAIAnalyst, IconCompetitors } from '../components/icons';
import './ReportsPage.css';
// =============================================================
// ReportsPage — Phase 7+ Step 4: Executive SEO Reporting Hub
// Multi-tenant SaaS — Deterministic, verifiable SEO reports
// generated from existing platform data. Zero external calls
// and zero simulated/fabricated metrics.
// =============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  businessApi,
  reportsApi,
  Business,
  ReportType,
  ReportPeriod,
  ReportCardDto,
  SeoOverviewReportDto,
  WebsiteAuditReportDto,
  SearchPerformanceReportDto,
  GbpPerformanceReportDto,
  SeoOpportunitiesReportDto,
  AiInsightsReportDto,
  CompetitorComparisonReportDto,
} from '../lib/api';

const REPORT_TABS: Array<{ type: ReportType; label: string; icon: React.ReactNode }> = [
  { type: 'seo_overview', label: 'SEO Overview', icon: <IconReports style={{ width: 20, height: 20, color: '#3b82f6' }} /> },
  { type: 'website_audit', label: 'Website Audit', icon: <IconWebsiteAudit style={{ width: 20, height: 20, color: '#0ea5e9' }} /> },
  { type: 'search_performance', label: 'Search Performance', icon: <IconGoogleSearch style={{ width: 20, height: 20, color: '#10b981' }} /> },
  { type: 'gbp_performance', label: 'GBP Performance', icon: <IconGoogleBusiness style={{ width: 20, height: 20, color: '#f43f5e' }} /> },
  { type: 'seo_opportunities', label: 'Opportunities', icon: <IconSEOIntelligence style={{ width: 20, height: 20, color: '#f59e0b' }} /> },
  { type: 'ai_insights', label: 'AI Insights', icon: <IconAIAnalyst style={{ width: 20, height: 20, color: '#8b5cf6' }} /> },
  { type: 'competitor_comparison', label: 'Competitors', icon: <IconCompetitors style={{ width: 20, height: 20, color: '#6366f1' }} /> },
];

export default function ReportsPage() {
  const { businessId, reportType: paramReportType } = useParams<{
    businessId?: string;
    reportType?: string;
  }>();
  const navigate = useNavigate();

  // Active Business & Business List
  const [business, setBusiness] = useState<Business | null>(null);
  const [businessList, setBusinessList] = useState<Business[]>([]);
  const [activeBizId, setActiveBizId] = useState<number | null>(
    businessId ? parseInt(businessId, 10) : null
  );

  // Active Report Selection & Period
  const [selectedReportType, setSelectedReportType] = useState<ReportType | null>(
    (paramReportType as ReportType) || null
  );
  const [period, setPeriod] = useState<ReportPeriod>('30d');
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');

  // Report Cards List State (Hub view)
  const [reportCards, setReportCards] = useState<ReportCardDto[]>([]);
  const [loadingCards, setLoadingCards] = useState<boolean>(true);
  const [cardsError, setCardsError] = useState<string | null>(null);

  // Active Detailed Report Data State
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [seoOverview, setSeoOverview] = useState<SeoOverviewReportDto | null>(null);
  const [websiteAudit, setWebsiteAudit] = useState<WebsiteAuditReportDto | null>(null);
  const [searchPerf, setSearchPerf] = useState<SearchPerformanceReportDto | null>(null);
  const [gbpPerf, setGbpPerf] = useState<GbpPerformanceReportDto | null>(null);
  const [opportunities, setOpportunities] = useState<SeoOpportunitiesReportDto | null>(null);
  const [aiInsights, setAiInsights] = useState<AiInsightsReportDto | null>(null);
  const [competitors, setCompetitors] = useState<CompetitorComparisonReportDto | null>(null);

  // Export state
  const [exportingType, setExportingType] = useState<string | null>(null);
  const [exportSuccessMsg, setExportSuccessMsg] = useState<string | null>(null);

  // Synchronize URL param with state
  useEffect(() => {
    if (paramReportType && REPORT_TABS.some((t) => t.type === paramReportType)) {
      setSelectedReportType(paramReportType as ReportType);
    } else {
      setSelectedReportType(null);
    }
  }, [paramReportType]);

  // Load business list & resolve active business
  useEffect(() => {
    let mounted = true;
    businessApi
      .list()
      .then((list) => {
        if (!mounted) return;
        setBusinessList(list);

        if (businessId) {
          const parsed = parseInt(businessId, 10);
          const found = list.find((b) => b.id === parsed);
          if (found) {
            setBusiness(found);
            setActiveBizId(parsed);
          } else if (list.length > 0) {
            navigate(`/businesses/${list[0].id}/reports`, { replace: true });
          }
        } else {
          const stored = localStorage.getItem('active_dashboard_business_id');
          const storedId = stored ? parseInt(stored, 10) : null;
          const found = storedId ? list.find((b) => b.id === storedId) : null;
          const target = found || list[0];
          if (target) {
            navigate(`/businesses/${target.id}/reports`, { replace: true });
          } else {
            setLoadingCards(false);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to load businesses:', err);
        setCardsError('Failed to load businesses');
        setLoadingCards(false);
      });

    return () => {
      mounted = false;
    };
  }, [businessId, navigate]);

  // Fetch report cards for the hub view
  const fetchReportCards = useCallback(async () => {
    if (!activeBizId) return;
    setLoadingCards(true);
    setCardsError(null);
    try {
      const res = await reportsApi.list(activeBizId);
      setReportCards(res.reports);
    } catch (err: any) {
      console.error('Failed to fetch report cards:', err);
      setCardsError(err?.message || 'Failed to load reports');
    } finally {
      setLoadingCards(false);
    }
  }, [activeBizId]);

  useEffect(() => {
    if (activeBizId) {
      fetchReportCards();
    }
  }, [activeBizId, fetchReportCards]);

  // Fetch detailed report when selectedReportType changes
  const fetchDetailedReport = useCallback(
    async (type: ReportType) => {
      if (!activeBizId) return;
      setLoadingReport(true);
      setReportError(null);

      const queryParams = {
        period,
        startDate: period === 'custom' && customStart ? customStart : undefined,
        endDate: period === 'custom' && customEnd ? customEnd : undefined,
      };

      try {
        switch (type) {
          case 'seo_overview': {
            const data = await reportsApi.getSeoOverview(activeBizId, queryParams);
            setSeoOverview(data);
            break;
          }
          case 'website_audit': {
            const data = await reportsApi.getWebsiteAudit(activeBizId, queryParams);
            setWebsiteAudit(data);
            break;
          }
          case 'search_performance': {
            const data = await reportsApi.getSearchPerformance(activeBizId, queryParams);
            setSearchPerf(data);
            break;
          }
          case 'gbp_performance': {
            const data = await reportsApi.getGbpPerformance(activeBizId, queryParams);
            setGbpPerf(data);
            break;
          }
          case 'seo_opportunities': {
            const data = await reportsApi.getSeoOpportunities(activeBizId, queryParams);
            setOpportunities(data);
            break;
          }
          case 'ai_insights': {
            const data = await reportsApi.getAiInsights(activeBizId, queryParams);
            setAiInsights(data);
            break;
          }
          case 'competitor_comparison': {
            const data = await reportsApi.getCompetitorComparison(activeBizId, queryParams);
            setCompetitors(data);
            break;
          }
        }
      } catch (err: any) {
        console.error(`Failed to load ${type} report:`, err);
        setReportError(err?.message || `Failed to load ${type} report`);
      } finally {
        setLoadingReport(false);
      }
    },
    [activeBizId, period, customStart, customEnd]
  );

  useEffect(() => {
    if (selectedReportType) {
      fetchDetailedReport(selectedReportType);
    }
  }, [selectedReportType, fetchDetailedReport]);

  // Handlers
  const handleSelectBusiness = (newId: number) => {
    setActiveBizId(newId);
    localStorage.setItem('active_dashboard_business_id', String(newId));
    window.dispatchEvent(new CustomEvent('active-business-change', { detail: newId }));
    if (selectedReportType) {
      navigate(`/businesses/${newId}/reports/${selectedReportType}`);
    } else {
      navigate(`/businesses/${newId}/reports`);
    }
  };

  const handleSelectReport = (type: ReportType | null) => {
    setSelectedReportType(type);
    if (!activeBizId) return;
    if (type) {
      navigate(`/businesses/${activeBizId}/reports/${type}`);
    } else {
      navigate(`/businesses/${activeBizId}/reports`);
    }
  };

  const handleExport = async (type: ReportType, format: 'csv' | 'json') => {
    if (!activeBizId) return;
    const exportKey = `${type}_${format}`;
    setExportingType(exportKey);
    setExportSuccessMsg(null);
    try {
      await reportsApi.downloadExport(activeBizId, type, format, {
        period,
        startDate: period === 'custom' && customStart ? customStart : undefined,
        endDate: period === 'custom' && customEnd ? customEnd : undefined,
      });
      setExportSuccessMsg(`Successfully exported ${type.replace(/_/g, ' ')} as ${format.toUpperCase()}`);
      setTimeout(() => setExportSuccessMsg(null), 4000);
    } catch (err: any) {
      alert(`Export failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setExportingType(null);
    }
  };

  const availableCount = useMemo(
    () => reportCards.filter((c) => c.available).length,
    [reportCards]
  );

  return (
    <DashboardLayout
      title="Reports & Analytics"
      subtitle={
        business
          ? `Executive SEO reports for ${business.name} — 100% verifiable data.`
          : 'Executive SEO reporting hub'
      }
    >
      <div className="reports-page">
        {/* Top Control Bar */}
        <div className="reports-toolbar">
          {/* Left: Context Badge & Back Button */}
          <div className="reports-toolbar-left">
            {selectedReportType ? (
              <button
                type="button"
                className="reports-back-btn"
                onClick={() => handleSelectReport(null)}
              >
                ← Back to Report Modules
              </button>
            ) : (
              <div className="reports-biz-badge">
                <span className="badge-icon">📊</span>
                <span>{business?.name || 'Verified Data'}</span>
                <span className="badge-verified">Platform Telemetry</span>
              </div>
            )}
          </div>

          {/* Right: Period Filter & Actions */}
          <div className="reports-toolbar-right">
            {/* Period Selector */}
            <div className="reports-period-selector">
              {(['7d', '30d', '90d', 'custom'] as ReportPeriod[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`reports-period-btn ${period === p ? 'active' : ''}`}
                >
                  {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : p === '90d' ? '90 Days' : 'Custom'}
                </button>
              ))}
            </div>

            {/* Custom Date Inputs */}
            {period === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="reports-date-input"
                />
                <span style={{ color: '#64748b', fontSize: '12px' }}>to</span>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="reports-date-input"
                />
              </div>
            )}

            {/* Print / PDF Button */}
            <button
              type="button"
              onClick={() => window.print()}
              title="Print or Save as PDF"
              className="reports-btn-action"
            >
              🖨️ Print / PDF
            </button>

            {/* Refresh */}
            <button
              type="button"
              onClick={() => {
                fetchReportCards();
                if (selectedReportType) fetchDetailedReport(selectedReportType);
              }}
              className="reports-btn-action"
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {/* Export Success Toast */}
        {exportSuccessMsg && (
          <div
            style={{
              background: '#ecfdf5',
              border: '1px solid #10b981',
              color: '#059669',
              borderRadius: '10px',
              padding: '12px 18px',
              marginBottom: '20px',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 1px 3px rgba(16, 185, 129, 0.1)',
            }}
          >
            <span>✓ {exportSuccessMsg}</span>
            <button
              type="button"
              onClick={() => setExportSuccessMsg(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#059669',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Global Error Banner */}
        {cardsError && (
          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #ef4444',
              color: '#dc2626',
              padding: '12px 18px',
              borderRadius: '10px',
              marginBottom: '20px',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            ⚠️ {cardsError}
          </div>
        )}

        {/* ======================================================== */}
        {/* VIEW A: REPORTS HUB (GRID OF 7 REPORT CARDS)            */}
        {/* ======================================================== */}
        {!selectedReportType && (
          <div>
            {/* Executive Summary Metrics Strip */}
            <div className="reports-summary-strip">
              <div className="reports-metric-card">
                <div className="reports-metric-label">REPORTS STATUS</div>
                <div className="reports-metric-val">
                  {availableCount} <span className="val-sub">/ {reportCards.length || 7} Ready</span>
                </div>
                <div className="reports-metric-sub">
                  ✓ 100% Stored Platform Telemetry
                </div>
              </div>

              <div className="reports-metric-card">
                <div className="reports-metric-label">ACTIVE TIMEFRAME</div>
                <div className="reports-metric-val" style={{ color: '#2563eb' }}>
                  {period === '7d' ? 'Last 7 Days' : period === '30d' ? 'Last 30 Days' : period === '90d' ? 'Last 90 Days' : 'Custom Dates'}
                </div>
                <div className="reports-metric-sub neutral">
                  Synchronized across all modules
                </div>
              </div>

              <div className="reports-metric-card">
                <div className="reports-metric-label">DATA INTEGRITY</div>
                <div className="reports-metric-val" style={{ fontSize: '1.25rem', marginTop: 4 }}>
                  Deterministic & Verified
                </div>
                <div className="reports-metric-sub">
                  ✓ Zero simulated metrics
                </div>
              </div>

              <div className="reports-metric-card">
                <div className="reports-metric-label">CLIENT DELIVERABLES</div>
                <div className="reports-metric-val" style={{ fontSize: '1.25rem', color: '#7c3aed', marginTop: 4 }}>
                  Executive Suite
                </div>
                <div className="reports-metric-sub neutral">
                  Instant CSV, JSON & PDF deliverable files
                </div>
              </div>
            </div>

            {/* Section Header */}
            <div className="reports-section-header">
              <h2 className="reports-section-title">
                Executive Report Modules
              </h2>
              <p className="reports-section-desc">
                Select an executive module to view deep performance breakdowns, audit findings, and strategic opportunities.
              </p>
            </div>

            {/* Report Cards Grid */}
            {loadingCards ? (
              <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                Loading executive report suite...
              </div>
            ) : (
              <div className="reports-grid">
                {reportCards.map((card) => (
                  <div key={card.reportType} className="report-card-v2">
                    <div>
                      {/* Top Card Bar */}
                      <div className="report-card-top-bar">
                        <div className={`report-card-icon-box ${card.reportType}`}>
                          {card.icon}
                        </div>
                        <span className={`report-card-badge ${card.available ? 'ready' : 'required'}`}>
                          {card.available ? '✓ Ready' : '⚠ Data Required'}
                        </span>
                      </div>

                      {/* Title & Description */}
                      <h3 className="report-card-title">{card.label}</h3>
                      <p className="report-card-desc">{card.description}</p>

                      {/* Data Note / Reason */}
                      {!card.available && card.unavailableReason && (
                        <div className="report-card-reason">
                          ℹ️ {card.unavailableReason}
                        </div>
                      )}

                      {card.lastDataTimestamp && (
                        <div className="report-card-sync">
                          <span>🕒 Last synced: {new Date(card.lastDataTimestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions Row */}
                    <div className="report-card-actions">
                      <button
                        type="button"
                        onClick={() => handleSelectReport(card.reportType)}
                        className="report-btn-view"
                      >
                        <span>View Full Report</span>
                        <span>→</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleExport(card.reportType, 'csv')}
                        disabled={exportingType === `${card.reportType}_csv`}
                        title="Download CSV"
                        className="report-btn-export"
                      >
                        {exportingType === `${card.reportType}_csv` ? '...' : 'CSV'}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleExport(card.reportType, 'json')}
                        disabled={exportingType === `${card.reportType}_json`}
                        title="Download JSON"
                        className="report-btn-export"
                      >
                        {exportingType === `${card.reportType}_json` ? '...' : 'JSON'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ======================================================== */}
        {/* VIEW B: DETAILED REPORT VIEW                            */}
        {/* ======================================================== */}
        {selectedReportType && (
          <div>
            {/* Quick Switcher Tabs */}
            <div className="reports-tab-nav">
              {REPORT_TABS.map((tab) => {
                const isActive = selectedReportType === tab.type;
                return (
                  <button
                    key={tab.type}
                    type="button"
                    onClick={() => handleSelectReport(tab.type)}
                    className={`reports-tab-btn ${isActive ? 'active' : ''}`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Report Header Bar */}
            <div className="reports-detail-header">
              <div>
                <h2 className="reports-detail-title">
                  {REPORT_TABS.find((t) => t.type === selectedReportType)?.icon}{' '}
                  {REPORT_TABS.find((t) => t.type === selectedReportType)?.label}
                </h2>
                <div className="reports-detail-meta">
                  Period: {period === '7d' ? 'Last 7 Days' : period === '30d' ? 'Last 30 Days' : period === '90d' ? 'Last 90 Days' : 'Custom Date Range'}{' '}
                  · Generated on demand from verified local database
                </div>
              </div>

              {/* Export Buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleExport(selectedReportType, 'csv')}
                  disabled={exportingType === `${selectedReportType}_csv`}
                  className="reports-btn-export-csv"
                >
                  {exportingType === `${selectedReportType}_csv` ? 'Exporting...' : '📥 Export CSV'}
                </button>
                <button
                  type="button"
                  onClick={() => handleExport(selectedReportType, 'json')}
                  disabled={exportingType === `${selectedReportType}_json`}
                  className="reports-btn-export-json"
                >
                  {exportingType === `${selectedReportType}_json` ? 'Exporting...' : '📥 Export JSON'}
                </button>
              </div>
            </div>

            {/* Report Content Loading or Error */}
            {loadingReport && (
              <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
                Generating report from verified data records...
              </div>
            )}

            {reportError && (
              <div
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid #ef4444',
                  color: '#fca5a5',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px',
                }}
              >
                ⚠️ Error generating report: {reportError}
              </div>
            )}

            {/* 1. SEO OVERVIEW REPORT */}
            {!loadingReport && selectedReportType === 'seo_overview' && seoOverview && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Top Metrics Cards */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                  }}
                >
                  <div
                    style={{
                      background: '#1e293b',
                      padding: '18px 20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>
                      WEBSITE SEO SCORE
                    </div>
                    <div
                      style={{
                        fontSize: '28px',
                        fontWeight: 700,
                        color:
                          (seoOverview.websiteAudit.overallScore ?? 0) >= 80
                            ? '#10b981'
                            : (seoOverview.websiteAudit.overallScore ?? 0) >= 50
                            ? '#f59e0b'
                            : '#ef4444',
                        marginTop: '4px',
                      }}
                    >
                      {seoOverview.websiteAudit.overallScore !== null
                        ? `${seoOverview.websiteAudit.overallScore} / 100`
                        : 'No Audit'}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                      {seoOverview.websiteAudit.pagesCrawled} pages crawled
                    </div>
                  </div>

                  <div
                    style={{
                      background: '#1e293b',
                      padding: '18px 20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>
                      SEARCH CLICKS
                    </div>
                    <div
                      style={{
                        fontSize: '28px',
                        fontWeight: 700,
                        color: '#38bdf8',
                        marginTop: '4px',
                      }}
                    >
                      {seoOverview.searchConsole.totalClicks.toLocaleString()}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                      {seoOverview.searchConsole.totalImpressions.toLocaleString()} impressions
                    </div>
                  </div>

                  <div
                    style={{
                      background: '#1e293b',
                      padding: '18px 20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>
                      GBP SEARCH VIEWS
                    </div>
                    <div
                      style={{
                        fontSize: '28px',
                        fontWeight: 700,
                        color: '#a78bfa',
                        marginTop: '4px',
                      }}
                    >
                      {seoOverview.gbp.searchImpressions.toLocaleString()}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                      {seoOverview.gbp.callClicks} phone calls generated
                    </div>
                  </div>

                  <div
                    style={{
                      background: '#1e293b',
                      padding: '18px 20px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>
                      ACTION OPPORTUNITIES
                    </div>
                    <div
                      style={{
                        fontSize: '28px',
                        fontWeight: 700,
                        color: '#fbbf24',
                        marginTop: '4px',
                      }}
                    >
                      {seoOverview.opportunities.open} Open
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>
                      {seoOverview.opportunities.critical} critical priority
                    </div>
                  </div>
                </div>

                {/* Website Health & Score Breakdown */}
                <div
                  style={{
                    background: '#1e293b',
                    borderRadius: '12px',
                    padding: '24px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0', color: '#f8fafc' }}>
                    🌐 Website Audit Pillars
                  </h3>
                  {seoOverview.websiteAudit.available ? (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '16px',
                      }}
                    >
                      {[
                        { label: 'Technical SEO', score: seoOverview.websiteAudit.technicalScore },
                        { label: 'On-Page SEO', score: seoOverview.websiteAudit.onPageScore },
                        { label: 'Local Signals', score: seoOverview.websiteAudit.localScore },
                        { label: 'Content Depth', score: seoOverview.websiteAudit.contentScore },
                      ].map((pillar) => (
                        <div
                          key={pillar.label}
                          style={{
                            background: '#0f172a',
                            borderRadius: '8px',
                            padding: '14px 16px',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>{pillar.label}</div>
                          <div
                            style={{
                              fontSize: '20px',
                              fontWeight: 700,
                              color:
                                (pillar.score ?? 0) >= 80
                                  ? '#10b981'
                                  : (pillar.score ?? 0) >= 50
                                  ? '#f59e0b'
                                  : '#ef4444',
                              marginTop: '4px',
                            }}
                          >
                            {pillar.score !== null ? `${pillar.score}%` : 'N/A'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ color: '#94a3b8', fontSize: '13px' }}>
                      No audit completed yet. Visit Website Audit to trigger your first crawl.
                    </div>
                  )}
                </div>

                {/* Search Performance Preview */}
                <div
                  style={{
                    background: '#1e293b',
                    borderRadius: '12px',
                    padding: '24px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0', color: '#f8fafc' }}>
                    🔍 Top Search Queries
                  </h3>
                  {seoOverview.searchConsole.topQueries.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', textAlign: 'left' }}>
                            <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Query</th>
                            <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Clicks</th>
                            <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Impressions</th>
                            <th style={{ padding: '8px 12px', color: '#94a3b8' }}>CTR</th>
                            <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Avg Position</th>
                          </tr>
                        </thead>
                        <tbody>
                          {seoOverview.searchConsole.topQueries.map((q) => (
                            <tr key={q.query} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                              <td style={{ padding: '10px 12px', color: '#f8fafc', fontWeight: 600 }}>
                                {q.query}
                              </td>
                              <td style={{ padding: '10px 12px', color: '#38bdf8' }}>{q.clicks}</td>
                              <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{q.impressions}</td>
                              <td style={{ padding: '10px 12px', color: '#94a3b8' }}>
                                {(q.ctr * 100).toFixed(2)}%
                              </td>
                              <td style={{ padding: '10px 12px', color: '#94a3b8' }}>
                                {q.position.toFixed(1)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ color: '#94a3b8', fontSize: '13px' }}>
                      {seoOverview.searchConsole.note}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 2. WEBSITE AUDIT REPORT */}
            {!loadingReport && selectedReportType === 'website_audit' && websiteAudit && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {!websiteAudit.available || !websiteAudit.audit ? (
                  <div
                    style={{
                      background: '#1e293b',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>🌐</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0', color: '#f8fafc' }}>
                      No Completed Audit Available
                    </h3>
                    <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '480px', margin: '0 auto 20px auto' }}>
                      {websiteAudit.noDataReason || 'Run a website audit to generate technical findings and score analysis.'}
                    </p>
                    <button
                      onClick={() => navigate(`/businesses/${activeBizId}/seo`)}
                      style={{
                        background: '#38bdf8',
                        color: '#0f172a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Go to Website Audit
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Scores Strip */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '16px',
                      }}
                    >
                      {[
                        { label: 'Overall Score', score: websiteAudit.audit.overallScore },
                        { label: 'Technical Score', score: websiteAudit.audit.technicalScore },
                        { label: 'On-Page Score', score: websiteAudit.audit.onPageScore },
                        { label: 'Local Signals', score: websiteAudit.audit.localScore },
                        { label: 'Content Depth', score: websiteAudit.audit.contentScore },
                        { label: 'Indexability', score: websiteAudit.audit.indexabilityScore },
                      ].map((item) => (
                        <div
                          key={item.label}
                          style={{
                            background: '#1e293b',
                            borderRadius: '12px',
                            padding: '18px 20px',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                          }}
                        >
                          <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>
                            {item.label.toUpperCase()}
                          </div>
                          <div
                            style={{
                              fontSize: '26px',
                              fontWeight: 700,
                              color:
                                (item.score ?? 0) >= 80
                                  ? '#10b981'
                                  : (item.score ?? 0) >= 50
                                  ? '#f59e0b'
                                  : '#ef4444',
                              marginTop: '4px',
                            }}
                          >
                            {item.score !== null ? `${item.score}%` : 'N/A'}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Top Findings */}
                    <div
                      style={{
                        background: '#1e293b',
                        borderRadius: '12px',
                        padding: '24px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                      }}
                    >
                      <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0', color: '#f8fafc' }}>
                        ⚠️ Top Audit Findings ({websiteAudit.audit.topFindings.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {websiteAudit.audit.topFindings.map((finding, idx) => (
                          <div
                            key={idx}
                            style={{
                              background: '#0f172a',
                              borderRadius: '8px',
                              padding: '16px',
                              border: '1px solid rgba(255, 255, 255, 0.05)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  background:
                                    finding.severity === 'CRITICAL'
                                      ? 'rgba(239, 68, 68, 0.2)'
                                      : finding.severity === 'WARNING'
                                      ? 'rgba(245, 158, 11, 0.2)'
                                      : 'rgba(56, 189, 248, 0.2)',
                                  color:
                                    finding.severity === 'CRITICAL'
                                      ? '#ef4444'
                                      : finding.severity === 'WARNING'
                                      ? '#f59e0b'
                                      : '#38bdf8',
                                }}
                              >
                                {finding.severity}
                              </span>
                              <span style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase' }}>
                                {finding.category}
                              </span>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: '#f8fafc' }}>
                                {finding.title}
                              </div>
                            </div>
                            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 8px 0' }}>
                              {finding.description}
                            </p>
                            <div style={{ fontSize: '12px', color: '#38bdf8', background: 'rgba(56, 189, 248, 0.05)', padding: '8px 12px', borderRadius: '6px' }}>
                              💡 <strong>Action:</strong> {finding.recommendation}
                            </div>
                            {finding.affectedUrl && (
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px' }}>
                                URL: {finding.affectedUrl}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* 3. SEARCH PERFORMANCE REPORT */}
            {!loadingReport && selectedReportType === 'search_performance' && searchPerf && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {!searchPerf.available ? (
                  <div
                    style={{
                      background: '#1e293b',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>🔍</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0', color: '#f8fafc' }}>
                      Search Console Data Unavailable
                    </h3>
                    <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '480px', margin: '0 auto 20px auto' }}>
                      {searchPerf.noDataReason || 'Connect Google Search Console in Google Integrations to sync impressions and queries.'}
                    </p>
                    <button
                      onClick={() => navigate(`/businesses/${activeBizId}/integrations`)}
                      style={{
                        background: '#38bdf8',
                        color: '#0f172a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Connect Google Search Console
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Summary Strip */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                      }}
                    >
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>TOTAL CLICKS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#38bdf8', marginTop: '4px' }}>
                          {searchPerf.summary?.totalClicks.toLocaleString() ?? 0}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>TOTAL IMPRESSIONS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#f8fafc', marginTop: '4px' }}>
                          {searchPerf.summary?.totalImpressions.toLocaleString() ?? 0}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>AVERAGE CTR</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginTop: '4px' }}>
                          {searchPerf.summary ? `${(searchPerf.summary.averageCtr * 100).toFixed(2)}%` : '0%'}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>AVERAGE POSITION</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#fbbf24', marginTop: '4px' }}>
                          {searchPerf.summary?.averagePosition.toFixed(1) ?? 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Top Queries Table */}
                    <div style={{ background: '#1e293b', borderRadius: '12px', padding: '24px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0', color: '#f8fafc' }}>
                        Top Search Queries ({searchPerf.topQueries.length})
                      </h3>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', textAlign: 'left' }}>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Query</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Clicks</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Impressions</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>CTR</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Avg Position</th>
                            </tr>
                          </thead>
                          <tbody>
                            {searchPerf.topQueries.map((q) => (
                              <tr key={q.query} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                <td style={{ padding: '10px 12px', color: '#f8fafc', fontWeight: 600 }}>{q.query}</td>
                                <td style={{ padding: '10px 12px', color: '#38bdf8' }}>{q.clicks}</td>
                                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{q.impressions}</td>
                                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{(q.ctr * 100).toFixed(2)}%</td>
                                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{q.position.toFixed(1)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* 4. GBP PERFORMANCE REPORT */}
            {!loadingReport && selectedReportType === 'gbp_performance' && gbpPerf && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {!gbpPerf.available ? (
                  <div
                    style={{
                      background: '#1e293b',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>📍</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0', color: '#f8fafc' }}>
                      GBP Data Unavailable
                    </h3>
                    <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '480px', margin: '0 auto 20px auto' }}>
                      {gbpPerf.noDataReason || 'Connect Google Business Profile in Google Integrations to sync calls, views, and direction requests.'}
                    </p>
                    <button
                      onClick={() => navigate(`/businesses/${activeBizId}/integrations`)}
                      style={{
                        background: '#38bdf8',
                        color: '#0f172a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Connect Google Business Profile
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '16px',
                      }}
                    >
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>SEARCH VIEWS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#38bdf8', marginTop: '4px' }}>
                          {gbpPerf.metrics?.searchImpressions.toLocaleString() ?? 0}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>MAPS VIEWS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#a78bfa', marginTop: '4px' }}>
                          {gbpPerf.metrics?.mapsImpressions.toLocaleString() ?? 0}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>PHONE CALLS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginTop: '4px' }}>
                          {gbpPerf.metrics?.callClicks.toLocaleString() ?? 0}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>WEBSITE CLICKS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#fbbf24', marginTop: '4px' }}>
                          {gbpPerf.metrics?.websiteClicks.toLocaleString() ?? 0}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>DIRECTIONS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#f43f5e', marginTop: '4px' }}>
                          {gbpPerf.metrics?.directionRequests.toLocaleString() ?? 0}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* 5. SEO OPPORTUNITIES REPORT */}
            {!loadingReport && selectedReportType === 'seo_opportunities' && opportunities && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {!opportunities.available ? (
                  <div
                    style={{
                      background: '#1e293b',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>💡</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0', color: '#f8fafc' }}>
                      No Opportunities Generated
                    </h3>
                    <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '480px', margin: '0 auto 20px auto' }}>
                      {opportunities.noDataReason || 'Visit SEO Intelligence to run the deterministic opportunity engine.'}
                    </p>
                    <button
                      onClick={() => navigate(`/businesses/${activeBizId}/intelligence`)}
                      style={{
                        background: '#38bdf8',
                        color: '#0f172a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Generate Opportunities
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '16px',
                      }}
                    >
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>OPPORTUNITY SCORE</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#38bdf8', marginTop: '4px' }}>
                          {opportunities.summary.opportunityScore} / 100
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>OPEN ACTIONS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#fbbf24', marginTop: '4px' }}>
                          {opportunities.summary.open}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>CRITICAL FIXES</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#ef4444', marginTop: '4px' }}>
                          {opportunities.summary.critical}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>COMPLETED</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', marginTop: '4px' }}>
                          {opportunities.summary.completed}
                        </div>
                      </div>
                    </div>

                    <div style={{ background: '#1e293b', borderRadius: '12px', padding: '24px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0', color: '#f8fafc' }}>
                        High Priority Action Items ({opportunities.topOpportunities.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {opportunities.topOpportunities.map((op) => (
                          <div
                            key={op.id}
                            style={{
                              background: '#0f172a',
                              borderRadius: '8px',
                              padding: '16px',
                              border: '1px solid rgba(255, 255, 255, 0.05)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  background:
                                    op.priority === 'CRITICAL'
                                      ? 'rgba(239, 68, 68, 0.2)'
                                      : op.priority === 'HIGH'
                                      ? 'rgba(245, 158, 11, 0.2)'
                                      : 'rgba(56, 189, 248, 0.2)',
                                  color:
                                    op.priority === 'CRITICAL'
                                      ? '#ef4444'
                                      : op.priority === 'HIGH'
                                      ? '#f59e0b'
                                      : '#38bdf8',
                                }}
                              >
                                {op.priority}
                              </span>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: '#f8fafc' }}>
                                {op.title}
                              </div>
                            </div>
                            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '0 0 8px 0' }}>
                              {op.description}
                            </p>
                            <div style={{ fontSize: '12px', color: '#38bdf8', background: 'rgba(56, 189, 248, 0.05)', padding: '8px 12px', borderRadius: '6px' }}>
                              💡 {op.recommendation}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* 6. AI INSIGHTS REPORT */}
            {!loadingReport && selectedReportType === 'ai_insights' && aiInsights && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {!aiInsights.available ? (
                  <div
                    style={{
                      background: '#1e293b',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>🤖</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0', color: '#f8fafc' }}>
                      No AI Insights Generated Yet
                    </h3>
                    <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '480px', margin: '0 auto 20px auto' }}>
                      {aiInsights.noDataReason || 'Generate AI analyses from SEO Intelligence to populate executive summaries.'}
                    </p>
                    <button
                      onClick={() => navigate(`/businesses/${activeBizId}/intelligence`)}
                      style={{
                        background: '#38bdf8',
                        color: '#0f172a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Open AI SEO Analyst
                    </button>
                  </div>
                ) : (
                  <>
                    {aiInsights.weeklySummary && (
                      <div style={{ background: '#1e293b', borderRadius: '12px', padding: '24px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 12px 0', color: '#f8fafc' }}>
                          🤖 Executive Performance Summary
                        </h3>
                        <p style={{ fontSize: '14px', color: '#f1f5f9', lineHeight: '1.6', margin: '0 0 16px 0' }}>
                          {aiInsights.weeklySummary.performanceSummary}
                        </p>
                        {aiInsights.weeklySummary.recommendedPriorities.length > 0 && (
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#38bdf8', marginBottom: '8px' }}>
                              Recommended Strategic Priorities:
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#94a3b8', fontSize: '13px', lineHeight: '1.6' }}>
                              {aiInsights.weeklySummary.recommendedPriorities.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* 7. COMPETITOR COMPARISON REPORT */}
            {!loadingReport && selectedReportType === 'competitor_comparison' && competitors && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {!competitors.available ? (
                  <div
                    style={{
                      background: '#1e293b',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <div style={{ fontSize: '36px', marginBottom: '12px' }}>⚔️</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px 0', color: '#f8fafc' }}>
                      No Competitors Tracked
                    </h3>
                    <p style={{ color: '#94a3b8', fontSize: '14px', maxWidth: '480px', margin: '0 auto 20px auto' }}>
                      {competitors.noDataReason || 'Add competitors in the Competitors module to run side-by-side SEO audits.'}
                    </p>
                    <button
                      onClick={() => navigate(`/businesses/${activeBizId}/competitors`)}
                      style={{
                        background: '#38bdf8',
                        color: '#0f172a',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Track Competitors
                    </button>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                      }}
                    >
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>YOUR SEO SCORE</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#38bdf8', marginTop: '4px' }}>
                          {competitors.businessSeoScore !== null ? `${competitors.businessSeoScore}%` : 'Not Audited'}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>COMPETITOR AVG SCORE</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#fbbf24', marginTop: '4px' }}>
                          {competitors.averageCompetitorScore !== null ? `${competitors.averageCompetitorScore}%` : 'N/A'}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>SCORE DIFFERENCE</div>
                        <div
                          style={{
                            fontSize: '28px',
                            fontWeight: 700,
                            color:
                              (competitors.scoreDifference ?? 0) >= 0 ? '#10b981' : '#ef4444',
                            marginTop: '4px',
                          }}
                        >
                          {competitors.scoreDifference !== null
                            ? `${competitors.scoreDifference > 0 ? '+' : ''}${competitors.scoreDifference} pts`
                            : 'N/A'}
                        </div>
                      </div>
                      <div style={{ background: '#1e293b', padding: '18px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 600 }}>TRACKED COMPETITORS</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: '#f8fafc', marginTop: '4px' }}>
                          {competitors.totalCompetitors}
                        </div>
                      </div>
                    </div>

                    <div style={{ background: '#1e293b', borderRadius: '12px', padding: '24px', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0', color: '#f8fafc' }}>
                        Competitor Benchmarking Table ({competitors.competitors.length})
                      </h3>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', textAlign: 'left' }}>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Competitor</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Domain</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>SEO Score</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>HTTPS</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Pages</th>
                              <th style={{ padding: '8px 12px', color: '#94a3b8' }}>Last Analyzed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {competitors.competitors.map((comp) => (
                              <tr key={comp.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                                <td style={{ padding: '10px 12px', color: '#f8fafc', fontWeight: 600 }}>{comp.name}</td>
                                <td style={{ padding: '10px 12px', color: '#38bdf8' }}>{comp.domain}</td>
                                <td style={{ padding: '10px 12px' }}>
                                  {comp.overallScore !== null ? (
                                    <span
                                      style={{
                                        color:
                                          comp.overallScore >= 80
                                            ? '#10b981'
                                            : comp.overallScore >= 50
                                            ? '#f59e0b'
                                            : '#ef4444',
                                        fontWeight: 700,
                                      }}
                                    >
                                      {comp.overallScore}%
                                    </span>
                                  ) : (
                                    <span style={{ color: '#64748b' }}>Not Analyzed</span>
                                  )}
                                </td>
                                <td style={{ padding: '10px 12px', color: comp.isHttps ? '#10b981' : '#ef4444' }}>
                                  {comp.isHttps ? '✓ Yes' : '✕ No'}
                                </td>
                                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{comp.pageCount ?? '—'}</td>
                                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>
                                  {comp.lastAnalyzedAt ? new Date(comp.lastAnalyzedAt).toLocaleDateString() : 'Never'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div
                        style={{
                          marginTop: '16px',
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          fontSize: '12px',
                          color: '#94a3b8',
                          lineHeight: '1.5',
                        }}
                      >
                        ℹ️ <strong>Honest Data Notice:</strong> Competitor scores are computed from publicly crawlable website signals (HTML structure, meta headers, HTTPS, performance). Private Google Search Console ranking data and private Google Business reviews cannot be legally or ethically accessed for third-party competitors and are never fabricated.
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
